import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import ResultCard, { type DisplayResult } from "./ResultCard";

export interface ResultModalProps {
  result: DisplayResult;
  templateName?: string;
  onReset: () => void;
  resetLabel?: string;
  resetHint?: string;
}

export default function ResultModal({
  result,
  templateName,
  onReset,
  resetLabel,
  resetHint,
}: ResultModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Save prior focused element
    const priorFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Body scroll lock (saving/restoring inline style only)
    const originalStyle = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Focus the dialog without scrolling to the first action control
    dialogRef.current?.focus({ preventScroll: true });
    if (dialogRef.current) {
      dialogRef.current.scrollTop = 0;
    }

    return () => {
      document.body.style.overflow = originalStyle;
      // Restore prior connected focus
      if (priorFocusedElement && document.body.contains(priorFocusedElement)) {
        priorFocusedElement.focus();
      }
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onReset();
        return;
      }
      if (e.key === "Tab") {
        if (!dialogRef.current) return;
        const focusables = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => {
          if (el instanceof HTMLButtonElement && el.disabled) return false;
          return el.offsetWidth > 0 || el.offsetHeight > 0;
        });

        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }

        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;

        if (e.shiftKey) {
          if (active === first || !dialogRef.current.contains(active)) {
            last.focus();
            e.preventDefault();
          }
        } else {
          if (active === last || !dialogRef.current.contains(active)) {
            first.focus();
            e.preventDefault();
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onReset]);

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="生成结果"
      tabIndex={-1}
      className="fixed inset-0 z-[60] overflow-y-auto bg-slate-900/50 p-4 outline-none"
      onClick={onReset}
    >
      <div className="min-h-full flex items-center justify-center">
        <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
          <ResultCard
            result={result}
            templateName={templateName}
            onReset={onReset}
            resetLabel={resetLabel}
            resetHint={resetHint}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}
